import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { orderAPI } from '../services/orderApi';
import { Client, ClientRequest, Order } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import { useOnboardingTour } from '../hooks/useOnboardingTour';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Eye, Search, AlertCircle, CheckCircle, X, Filter, HelpCircle } from 'lucide-react';

const ClientListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchClientNumber, setSearchClientNumber] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreTriggerRef, setLoadMoreTriggerRef] = useState<HTMLDivElement | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const { startTour, manualStart } = useOnboardingTour();

  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      loadClients();
    } else {
      setClients([]);
      setLoading(false);
    }
  }, [selectedCompany.selectedCompanyId]);

  // Effect for loading clients when page changes (infinite scroll)
  useEffect(() => {
    if (selectedCompany.selectedCompanyId && currentPage > 0) {
      loadMoreClients();
    }
  }, [currentPage]);

  // Auto-scroll infinite loading with Intersection Observer
  useEffect(() => {
    if (!loadMoreTriggerRef || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreTriggerRef);

    return () => {
      if (loadMoreTriggerRef) {
        observer.unobserve(loadMoreTriggerRef);
      }
    };
  }, [loadMoreTriggerRef, hasMore, isLoadingMore]);

  // Check for success message from navigation
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-start onboarding tour on first visit
  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      startTour({
        tourId: 'client-list',
        steps: [
          {
            element: '#client-list-create-btn',
            popover: {
              title: t('onboarding.clientList.createBtnTitle'),
              description: t('onboarding.clientList.createBtnDesc'),
              side: 'bottom' as const,
              align: 'end' as const,
            },
          },
          {
            element: '#client-list-table',
            popover: {
              title: t('onboarding.clientList.tableTitle'),
              description: t('onboarding.clientList.tableDesc'),
              side: 'top' as const,
              align: 'start' as const,
            },
          },
        ],
        autoStartDelay: 800,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany.selectedCompanyId]);

  const loadClients = async () => {
    if (!selectedCompany.selectedCompanyId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await clientAPI.searchClientsAdvanced({
        companyId: selectedCompany.selectedCompanyId,
        name: searchTerm || undefined,
        clientNumber: searchClientNumber ? Number(searchClientNumber) : undefined,
        email: searchEmail || undefined,
        page: 0,
        size: pageSize
      });

      setClients(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      setHasMore(!response.last);
      setCurrentPage(0);
    } catch (err) {
      setError(t('clients.failedToLoad'));
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreClients = async () => {
    if (!selectedCompany.selectedCompanyId || isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const response = await clientAPI.searchClientsAdvanced({
        companyId: selectedCompany.selectedCompanyId,
        name: searchTerm || undefined,
        clientNumber: searchClientNumber ? Number(searchClientNumber) : undefined,
        email: searchEmail || undefined,
        page: currentPage,
        size: pageSize
      });

      setClients(prev => [...prev, ...response.content]);
      setHasMore(!response.last);
    } catch (err) {
      console.error('Error loading more clients:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };



  const handleUpdateClient = async (clientId: string, clientData: ClientRequest) => {
    try {
      const updatedClient = await clientAPI.updateClient(clientId, clientData);
      setClients(clients.map(client =>
        client.id === clientId ? updatedClient : client
      ));
      setShowEditModal(false);
      setEditingClient(null);
      setError(null);
    } catch (err) {
      setError(t('clients.failedToUpdate'));
      console.error('Error updating client:', err);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm(t('clients.confirmDelete'))) {
      try {
        await clientAPI.deleteClient(clientId);
        setClients(clients.filter(client => client.id !== clientId));
        setError(null);
      } catch (err) {
        setError(t('clients.failedToDelete'));
        console.error('Error deleting client:', err);
      }
    }
  };

  const handleSearch = () => {
    loadClients();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSearchClientNumber('');
    setSearchEmail('');
    loadClients();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    loadClients();
  };

  const filteredClients = clients;

  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
            <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('clients.title')}</h1>
          {selectedCompany.selectedCompany && (
            <p className="text-gray-600 mt-1">{t('clients.subtitle', { companyName: selectedCompany.selectedCompany.name })}</p>
          )}
        </div>
        <div id="client-list-create-btn" className="flex gap-2">
          <button
            onClick={() => navigate('/clients/create')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            {t('clients.createClient')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5" />
            <span>{successMessage}</span>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-700 hover:text-green-900"
          >
            ×
          </button>
        </div>
      )}

      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
            <input
              type="text"
              placeholder={t('clients.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <Filter size={20} />
            {t('clients.filters')}
          </button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.clientNumber')}
              </label>
              <input
                type="text"
                value={searchClientNumber}
                onChange={(e) => setSearchClientNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={t('clients.clientNumberPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.email')}
              </label>
              <input
                type="text"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={t('clients.emailPlaceholder')}
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('clients.applyFilters')}
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('clients.clearFilters')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div id="client-list-table" className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.name')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.clientNumber')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.email')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.phone')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.city')}
              </th>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('clients.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredClients.map((client) => (
              <tr key={client.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{client.name}</div>
                  {client.ice && (
                    <div className="text-sm text-gray-500">{t('clients.ice')}: {client.ice}</div>
                  )}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.clientNumber || '-'}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.email || '-'}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.phone || '-'}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {client.city || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedClient(client);
                        setShowViewModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingClient(client);
                        setShowEditModal(true);
                      }}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {clients.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('clients.noClientsFound')}</p>
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      <div ref={setLoadMoreTriggerRef} className="h-10 flex items-center justify-center mt-4">
        {isLoadingMore && (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        )}
        {!hasMore && clients.length > 0 && (
          <p className="text-gray-500 text-sm">{t('clients.noMoreClients')}</p>
        )}
      </div>



      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <ClientModal
          mode="edit"
          client={editingClient}
          onClose={() => {
            setShowEditModal(false);
            setEditingClient(null);
          }}
          onSubmit={(data) => handleUpdateClient(editingClient.id, data)}
        />
      )}

      {/* View Client Modal */}
      {showViewModal && selectedClient && (
        <ClientViewModal
          client={selectedClient}
          onClose={() => {
            setShowViewModal(false);
            setSelectedClient(null);
          }}
        />
      )}

      {/* Onboarding Help Button */}
      <button
        type="button"
        className="onboarding-help-btn"
        title={t('onboarding.replayTour')}
        onClick={() => manualStart({
          tourId: 'client-list',
          steps: [
            {
              element: '#client-list-create-btn',
              popover: {
                title: t('onboarding.clientList.createBtnTitle'),
                description: t('onboarding.clientList.createBtnDesc'),
                side: 'bottom' as const,
                align: 'end' as const,
              },
            },
            {
              element: '#client-list-table',
              popover: {
                title: t('onboarding.clientList.tableTitle'),
                description: t('onboarding.clientList.tableDesc'),
                side: 'top' as const,
                align: 'start' as const,
              },
            },
          ],
        })}
      >
        <HelpCircle size={22} />
      </button>
    </Layout>
  );
};

// Client Modal Component
interface ClientModalProps {
  mode: 'create' | 'edit';
  client?: Client;
  onClose: () => void;
  onSubmit: (data: ClientRequest) => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ mode, client, onClose, onSubmit }) => {
  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const [formData, setFormData] = useState<ClientRequest>({
    companyId: selectedCompany.selectedCompanyId || '',
    clientNumber: client?.clientNumber || 0,
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    ice: client?.ice || '',
    street: client?.street || '',
    city: client?.city || '',
    postalCode: client?.postalCode || '',
    country: client?.country || '',
    website: client?.website || '',
  });


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">
          {mode === 'create' ? t('clients.createNew') : t('clients.editClient')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.clientNumber')}
              </label>
              <input
                type="number"
                min="0"
                value={formData.clientNumber || ''}
                onChange={(e) => setFormData({ ...formData, clientNumber: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={t('clients.clientNumberPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.name')} *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.email')} *
              </label>
              <input
                type="email"
                required
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.phone')} *
              </label>
              <input
                type="tel"
                required
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.ice')} *
              </label>
              <input
                type="text"
                required
                value={formData.ice || ''}
                onChange={(e) => setFormData({ ...formData, ice: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.street')}
              </label>
              <input
                type="text"
                value={formData.street || ''}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.city')}
              </label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.postalCode')}
              </label>
              <input
                type="text"
                value={formData.postalCode || ''}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.country')}
              </label>
              <input
                type="text"
                value={formData.country || ''}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clients.website')}
              </label>
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className={`flex justify-end ${isRTL ? 'space-x-reverse' : ''} space-x-3 pt-4`}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('clients.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {mode === 'create' ? t('clients.create') : t('clients.update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client View Modal Component
interface ClientViewModalProps {
  client: Client;
  onClose: () => void;
}

const ClientViewModal: React.FC<ClientViewModalProps> = ({ client, onClose }) => {
  const { t, isRTL } = useI18n();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await orderAPI.getOrdersByClient(client.id);
        setOrders(data);
      } catch (err) {
        console.error('Error fetching client orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [client.id]);

  const totalTransactions = orders.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalPaid = orders.reduce((sum, order) => {
    const paid = order.paymentHistory.reduce((pSum, p) => pSum + p.amount, 0);
    return sum + paid;
  }, 0);
  const remainingBalance = totalTransactions - totalPaid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">{t('clients.clientDetails')}</h2>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.name')}</label>
              <p className="text-sm text-gray-900">{client.name}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.clientNumber')}</label>
              <p className="text-sm text-gray-900">{client.clientNumber || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.email')}</label>
              <p className="text-sm text-gray-900">{client.email || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.phone')}</label>
              <p className="text-sm text-gray-900">{client.phone || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.ice')}</label>
              <p className="text-sm text-gray-900">{client.ice || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.street')}</label>
              <p className="text-sm text-gray-900">{client.street || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.city')}</label>
              <p className="text-sm text-gray-900">{client.city || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.postalCode')}</label>
              <p className="text-sm text-gray-900">{client.postalCode || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clients.country')}</label>
              <p className="text-sm text-gray-900">{client.country || '-'}</p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">{t('clients.website')}</label>
              <p className="text-sm text-gray-900">{client.website || '-'}</p>
            </div>
          </div>

          <div className="border-t pt-4 mt-6">
            <h3 className="text-lg font-bold mb-3">{t('clients.financialSummary')}</h3>
            <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.totalTransactions')}</label>
                <p className="text-lg font-bold text-gray-900">{totalTransactions.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.totalPaid')}</label>
                <p className="text-lg font-bold text-green-600">{totalPaid.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.remainingBalance')}</label>
                <p className="text-lg font-bold text-red-600">{remainingBalance.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-6">
            <h3 className="text-lg font-bold mb-3">{t('clients.orderHistory')}</h3>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : orders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>{t('invoices.date')}</th>
                      <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>{t('stock.orders.status')}</th>
                      <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase text-right`}>{t('invoices.total')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {new Date(order.creationDate).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            order.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                            {t(`stock.orders.${order.status.toLowerCase()}`)}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                          {order.totalAmount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">{t('clients.noOrdersFound')}</p>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              {t('clients.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientListPage;
